// app/javascript/projects/components/ProjectsDisplay.jsx

import React, { useContext } from 'react';
import { Button } from 'ui';
// TimeUtil
import TimeUtil from 'utils/TimeUtil';
import MemoAvatar from './MemoAvatar';
import { useModal } from './modal/MemoModalProvider';
import AtMentionMemo from 'memos/AtMentionMemo';
import { useUser } from 'hooks/users/queries';
import { useUserColor } from 'hooks/users/useUserColor';
import FallbackNameMapper from 'mappers/FallbackNameMapper';
import ProjectContext from 'contexts/ProjectContext';

import {
  ULTRA_LIGHT_GREY,
  LIGHT_GREY,
  ACTIVE_BUTTON_BLUE
} from 'constants/ColorConstants';
import HoverX from './HoverX';

const {
  AVATAR_SIZE
} = require('constants/DimensionsConstants');


const LINE_CLAMP_STYLE = {
  overflow: "hidden",
  display: "-webkit-box",
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: 2,
  LineClamp: 2,
  position: "relative",
}

const UNCLAMPED_STYLE = {

}

// Helper function to get user display data with proper fallbacks
function getUserDisplayData(userId, userData) {
  // Use the existing FallbackNameMapper with our injected state
  const mappedUser = FallbackNameMapper({
    entities: {
      users: {
        [userId]: userData
      }
    }
  }, userId) || {};
  
  return mappedUser;
}

function Memo(props) {
  const time = TimeUtil.getDisplayTime(props.time);
  const [isOverflow, setIsOverflow] = React.useState(false);
  const [isHovering, setIsHovering] = React.useState(false);
  const [isDeleteHovering, setIsDeleteHovering] = React.useState(false);
  
  const projectContext = useContext(ProjectContext);
  const projectId = projectContext?.project?.id;
  
  // Fetch user data using the user_id
  const { data: user } = useUser(props.user_id, projectId);
  
  // Get user color based on hash of user ID
  const userColor = useUserColor(props.user_id);
  
  // Get user display info with fallback logic
  const userData = getUserDisplayData(props.user_id, user);
  
  const measuredRef = React.useCallback(node => {
    if (node !== null) 
      setIsOverflow(node.scrollHeight > node.clientHeight);
  }, []);

  const style = props.isExpanded ? UNCLAMPED_STYLE : LINE_CLAMP_STYLE;
  const showSeeMoreButton = isOverflow && !props.isExpanded;
  const { openModal } = useModal();

  // Don't show delete button for temporary/pending memos
  const showDeleteButton = props.hasEditPermission && !props.isPending;
  
  return (
    <div
      style={{marginTop: '15px'}}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      <div className="memo">
        <div style={{ display: 'flex' }}>  
          <div style={{width: AVATAR_SIZE, marginTop: '2px'}}>
            <MemoAvatar initials={userData.initials} color={userColor || ACTIVE_BUTTON_BLUE}/>
          </div>
      
          <div style={{flexGrow: 1, paddingLeft: '5px'}}>
            <div className="memo-author" style={{
                fontSize: '.8em',
                display: 'inline-block',
                maxWidth: '100%',
                paddingLeft: '5px',
                paddingRight: '5px',
                marginRight: '5px',
                backgroundColor: isDeleteHovering ? ULTRA_LIGHT_GREY : null,
                borderRadius: '5px',
              }}>
                <strong>{userData.name}</strong>
                <span style={{color: LIGHT_GREY, paddingLeft: '10px'}}>{time}</span>
            </div>
            <div className="memo-text" ref={measuredRef} style={{
              whiteSpace: "pre-line",
              paddingLeft: '5px',
              ...style
            }}>
              <AtMentionMemo text={props.text} />
              {
              showSeeMoreButton &&
              <div
                style={{
                  position: 'absolute',
                  bottom: 0,
                  right: 0,
                  paddingLeft: '10px',
                  backgroundImage: 'linear-gradient(to right, transparent, white 20%)',
                }}
              >
                <Button 
                  variant="link"
                  style={{
                    margin: 0,
                    padding: 0,
                    fontSize: '.8em',
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    props.memoExpanded(props.id)
                    }
                  }
                >
                  ...See More
                </Button>
              </div>
            }
            </div>
          </div>

          {showDeleteButton &&
            <div style={{width: '20px', flexShrink: 0, flexGrow: 0}}>
              <HoverX
                className="delete-memo"
                show={isHovering}
                onClick={() => openModal(props.id, props.excerptId)}
                isHovering={setIsDeleteHovering}
              />
            </div>
          }
        </div>
      </div>
    </div>
  );
}

export default Memo;